import { HttpEventType } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { InvoiceEnums } from 'src/app/enum/invoice-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AttachmentModel } from 'src/app/models/tables/global/attachment-table.model';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-list-invoice-attachments',
  templateUrl: './list-invoice-attachments.component.html',
  styleUrls: ['./list-invoice-attachments.component.scss'],
  standalone: false
})
export class ListInvoiceAttachmentsComponent implements OnInit, OnDestroy {

  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<AttachmentModel> = new PagedResponseModel<AttachmentModel>();
  invoiceId: number = 0;
  invoiceStateId: number = 0;
  fileName: string = "";
  filePath: SafeUrl = "";
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  page: number = 1;
  permissionUpdatable: boolean = false;
  suscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Descargar',
      icon: 'pi pi-download',
      action: 'download'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    }
  ];

  constructor(public config: DynamicDialogConfig,
    private invoiceService: InvoiceService,
    private domSanitizer: DomSanitizer,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.invoiceId = inputs.invoiceId;
      this.invoiceStateId = inputs.invoiceStateId;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });
    await this.getColumns();
    await this.getAllInvoiceAttachments();
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async attachFiles() {
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;
    if (this.invoiceStateId != InvoiceEnums.abierta) {
      this.toastHandlerService.showToast(
        'error',
        'Adjuntos solo pueden ser agregados cuando la factura se encuentra abierta.'
      );
      return;
    }
    document.getElementById("files")?.click();
  }

  async chooseFile(event: any) {
    const file = event.target.files[0];
    if (file != null) {
      const formData = new FormData();
      formData.append('files', file);
      try {
        await firstValueFrom(this.invoiceService.attachFilesToInvoice(this.invoiceId, formData));
        this.getAllInvoiceAttachments();
        this.toastHandlerService.showToast(
          'success',
          'El archivo se adjunto exitosamente.'
        );
      } catch (error) {
        console.error('Error al adjuntar archivo a la devolución.', error);
      }
    }
    // Limpia el campo de archivos
    event.target.value = null;
  }

  async getAllInvoiceAttachments() {
    try {
      this.detailData = await firstValueFrom(
        this.invoiceService.getAllAttachmentsWithPaginationAsync(this.invoiceId, this.page, this.limit, this.columnSort, this.ascendant, [])
      );
    } catch (error) {
      console.error('Error al obtener los adjuntos.', error);
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.invoiceService.getColumnsForAttachmentsAsync());
    } catch (error) {
      console.error('Error al buscar columnas para los adjuntos.', error);
    }
  }

  async onAction(event: { action: string, item?: AttachmentModel, selectedItems?: number[] }) {
    if (event.action === 'download' && event.item) {
      try {
        this.filePath = this.sanitizeImageUrl(event.item.path);
        this.fileName = event.item.path.replace(/^.*[\\/]/, '');
        if (this.invoiceId != 0) {
          this.invoiceService.downloadFileToInvoice(this.invoiceId, this.fileName).subscribe(data => {
            switch (data.type) {
              case HttpEventType.Response: {
                const downloadedFile = new Blob([data.body!], { type: data.body!.type });
                const a = document.createElement('a');
                a.setAttribute('style', 'display:none;');
                document.body.appendChild(a);
                a.download = this.fileName;
                a.href = URL.createObjectURL(downloadedFile);
                a.target = '_blank';
                a.click();
                document.body.removeChild(a);
                break;
              }
            }
          });
        }
      } catch (error) {
        console.error('Error al descargar el archivo.', error);
      }
    }
    if (event.action === 'delete' && event.item) {
      const canUpdate = await this.validatePermissionUpdatable();
      if (!canUpdate) return;
      if (this.invoiceStateId != InvoiceEnums.abierta) {
        this.toastHandlerService.showToast(
          'error',
          'Adjuntos solo pueden ser eliminados cuando la factura se encuentra abierta.'
        );
        return;
      }
      Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea eliminar el adjunto seleccionado?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const file = event.item!.path.replace(/^.*[\\/]/, '');
          try {
            await firstValueFrom(this.invoiceService.deleteFilesToInvoice(this.invoiceId, file));
            this.toastHandlerService.showToast(
              'success',
              'El adjunto fue eliminado exitosamente.'
            );
            this.getAllInvoiceAttachments();
          } catch (error) {
            console.error('Error al eliminar el adjunto.', error);
          }
        }
      });
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllInvoiceAttachments();
  }

  sanitizeImageUrl(imageUrl: string): SafeUrl {
    return this.domSanitizer.bypassSecurityTrustUrl(imageUrl);
  }

  async validatePermissionUpdatable(): Promise<boolean> {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar Devoluciones. Comuníquese con el administrador del sistema.'
      );
      return false;
    }
    return true;
  }

}
